import React, { useContext } from 'react'
import classNames from 'classnames'
import { Card, Text, Icon } from '@toasttab/shared/src/utils/craftUtils'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { ProductCardProps } from './types.d'

const ProductCard = ({
  bgColor,
  cardBorderColor,
  colStart,
  column,
  ctaLabel,
  badge,
  elevation,
  linkIcons = 'none',
  parentType,
  products,
  textAlignment,
  toggleSwitch2, // Hover shadow
  dataTestId = 'ProductCard-1'
}: ProductCardProps) => {
  const context = useContext(I18nextContext)

  const product = products[0]
  const isLink = product.ctaLink && (toggleSwitch2 || ctaLabel)
  const CardEl = isLink ? 'a' : 'div'

  const cardTextAlign = Text.alignment(textAlignment)
  const cardBg = Card.backgroundColor(bgColor)
  const cardElevation = Card.elevation(elevation)
  const borderColor = Card.borderColor(cardBorderColor)
  const cardBorder = borderColor ? `border border-solid ${borderColor}` : ''

  const cardClasses = classNames(
    `h-full rounded-lg ${colStart} ${cardBg} ${cardBorder} ${cardElevation}`,
    {
      'swiper-slide':
        column?.toggleSwitch || (parentType && parentType === 'slider'),
      'hover:shadow-3xl': toggleSwitch2,
      'block cursor-pointer': isLink
    }
  )

  return (
    <CardEl
      className={cardClasses}
      data-testid={dataTestId}
      href={
        product.ctaLink
          ? localizeLink(product.ctaLink, context.language, context)
          : null
      }
    >
      <div
        className={`${cardTextAlign} py-8 px-4 md:py-6 md:px-6 leading-none text-[0px]`}
      >
        {product.imageResolutions?.length > 0 && (
          <div className='overflow-hidden'>
            <ImageResolutions
              classes='mb-6 md:mb-8 mx-auto !rounded-lg h-[172px] sm:h-[180px] max-w-[360px]'
              images={product}
            />
          </div>
        )}
        {badge ? (
          <Badge
            badgeColor='bg-yellow-25'
            classes='mb-4'
            text={badge}
            uppercase
          />
        ) : null}
        {product.sectionHeading ? (
          <Typography
            allowHtml
            as='h4'
            classes='mb-4 text-default'
            variation='h4'
          >
            {product.sectionHeading}
          </Typography>
        ) : null}
        {product.sectionDescription ? (
          <Typography allowHtml as='p' classes='text-gray-100'>
            {product.sectionDescription}
          </Typography>
        ) : null}
        {ctaLabel ? (
          <Typography allowHtml={false} as='div' classes='mt-4'>
            <TextLink
              description={`${product.sectionHeading} ${ctaLabel} `}
              el='span'
              icon={Icon.linkIcons(linkIcons)}
              text={ctaLabel}
            />
          </Typography>
        ) : null}
      </div>
    </CardEl>
  )
}

export default ProductCard
